import { useEffect, useMemo, useReducer, useState } from 'react';
import { BookFormat } from '@masters-club/types';
import { useParams } from 'react-router-dom';
import { useCustomMutation, useGo, useList, useOne, useUpdate } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Library, CheckCircle2, Loader2, Pencil, BookOpen, Info, DollarSign, Users, StickyNote, Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormHeader, FormStickyActions } from '@/components/forms';
import { TabsWithPersistence, ContentTagsField } from '@/components/shared';
import type { TranslationLanguage } from '@/components/translations';
import { notify } from '@/lib/notify';
import {
  BookBasicForm,
  bookBasicReducer,
  initBookState,
  toBookSlug,
} from './components/BookBasicForm';
import { BookChaptersTab } from './components/BookChaptersTab';
import { BookOverviewTab } from './components/BookOverviewTab';
import { BookPricingTab } from './components/BookPricingTab';
import { BookAccessTab } from './components/BookAccessTab';
import { BookNotesTab } from './components/BookNotesTab';
import { BookBookmarksTab } from './components/BookBookmarksTab';
import { BookProcessingStatusBadge } from './components/BookProcessingStatus';
import type { Book, BookChapterData, BookTranslationData } from './types';

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flagEmoji?: string | null;
  flag?: string | null;
}

export function BooksEdit() {
  const { id } = useParams<{ id: string }>();
  const bookId = id ? parseInt(id, 10) : 0;
  const go = useGo();
  const queryClient = useQueryClient();

  const { result, query } = useOne<Book>({
    resource: 'books',
    id: bookId,
    queryOptions: { enabled: bookId > 0 },
  });
  const book = result;

  const { result: languagesResult } = useList<LanguageRecord>({ resource: 'languages' });
  const languages: TranslationLanguage[] = (languagesResult?.data ?? []).map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flag: l.flagEmoji ?? l.flag ?? '',
  }));
  const [state, dispatch] = useReducer(bookBasicReducer, undefined, () => initBookState());
  const [translations, setTranslations] = useState<BookTranslationData[]>([]);
  const [chapters, setChapters] = useState<BookChapterData[]>([]);
  const [activeLanguage, setActiveLanguage] = useState('ru');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (book) {
      dispatch({ type: 'RESET', state: initBookState(book) });
      setTranslations(book.translations ?? []);
      setChapters(book.chapters ?? []);
    }
  }, [book]);

  const { mutate: updateMutation, mutation: updateMutationResult } = useUpdate<Book>();
  const isSaving = updateMutationResult.isPending;
  const publishMutation = useCustomMutation();

  const isAudio = state.format === BookFormat.AUDIO;

  // Для аудиокниги суммируем длительность по главам автоматически
  const totalDurationSec = isAudio
    ? chapters.reduce((sum, c) => sum + (c.durationSec ?? 0), 0) || null
    : state.totalDurationSec;

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!state.slug.trim()) { errs.slug = 'Slug обязателен'; }
    if (!isAudio && state.sourceFile === null) { errs.sourceFile = 'Загрузите исходный файл'; }
    if (translations.length === 0) {
      errs.ruTitle = 'Нужен хотя бы один перевод с названием';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) { return; }
    if (!isAudio && state.sourceFile === null) { return; }

    const data = {
      slug: toBookSlug(state.slug),
      coverFileId: state.cover?.id ?? null,
      sourceFileId: isAudio ? null : state.sourceFile?.id ?? null,
      author: state.author.trim() || null,
      languageCode: state.languageCode,
      translationGroupId: state.translationGroupId,
      pricingTierId: state.pricingTierId,
      totalPages: state.totalPages,
      totalDurationSec,
      isPublished: state.isPublished,
      translations,
    };

    updateMutation(
      { resource: 'books', id: bookId, values: data },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['books'] });
        },
        onError: (err) => {
          notify.error(err instanceof Error ? err.message : 'Ошибка сохранения');
        },
      },
    );
  };

  const handlePublish = (isPublished: boolean) => {
    publishMutation.mutate(
      { url: `/admin/books/${bookId}/publish`, method: 'post', values: { isPublished } },
      {
        onSuccess: () => {
          notify.success(isPublished ? 'Книга опубликована' : 'Публикация снята');
          void query?.refetch();
        },
        onError: (err) => {
          notify.error(err instanceof Error ? err.message : 'Ошибка публикации');
        },
      },
    );
  };

  const tabs = useMemo(
    () => {
      if (!book) { return []; }
      return [
        {
          value: 'overview',
          label: 'Обзор',
          icon: <Info className="h-4 w-4" />,
          content: <BookOverviewTab book={book} />,
        },
        {
          value: 'basic',
          label: 'Основное',
          icon: <Pencil className="h-4 w-4" />,
          content: (
            <BookBasicForm
              state={state}
              dispatch={dispatch}
              isEdit
              errors={errors}
              translations={{
                translations,
                onTranslationsChange: setTranslations,
                languages,
                activeLanguage,
                onActiveLanguageChange: setActiveLanguage,
              }}
            />
          ),
        },
        {
          value: 'pricing',
          label: 'Тарифы',
          icon: <DollarSign className="h-4 w-4" />,
          content: <BookPricingTab bookId={bookId} />,
        },
        {
          value: 'chapters',
          label: 'Главы',
          icon: <BookOpen className="h-4 w-4" />,
          content: (
            <BookChaptersTab
              bookId={bookId}
              format={book.format}
              chapters={chapters}
              onChange={setChapters}
              languages={languages}
            />
          ),
        },
        {
          value: 'access',
          label: 'Доступы',
          icon: <Users className="h-4 w-4" />,
          content: <BookAccessTab bookId={bookId} />,
        },
        {
          value: 'notes',
          label: 'Заметки',
          icon: <StickyNote className="h-4 w-4" />,
          content: <BookNotesTab bookId={bookId} />,
        },
        {
          value: 'bookmarks',
          label: 'Закладки',
          icon: <Bookmark className="h-4 w-4" />,
          content: <BookBookmarksTab bookId={bookId} />,
        },
      ];
    },
    [
      book,
      state,
      errors,
      translations,
      languages,
      activeLanguage,
      chapters,
      bookId,
    ],
  );

  if (query?.isLoading || !book) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      <FormHeader
        title={book.slug}
        description="Редактирование книги"
        sticky
        icon={<Library className="h-5 w-5" />}
        actions={
          <div className="flex items-center gap-2">
            <BookProcessingStatusBadge
              status={book.processingStatus}
              error={book.processingError}
            />
            {book.isPublished ? (
              <Button
                variant="outline"
                onClick={() => handlePublish(false)}
                disabled={publishMutation.mutation.isPending}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Снять публикацию
              </Button>
            ) : (
              <Button
                onClick={() => handlePublish(true)}
                disabled={publishMutation.mutation.isPending}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Опубликовать
              </Button>
            )}
          </div>
        }
      />

      <TabsWithPersistence
        tabs={tabs}
        defaultTab="overview"
        persistKey="books-edit"
        tabsListClassName="flex w-full overflow-x-auto whitespace-nowrap lg:inline-flex lg:w-auto"
      />

      <div className="rounded-lg border bg-card p-4">
        <ContentTagsField entityType="BOOK" entityId={bookId} />
      </div>

      <FormStickyActions>
        <Button variant="outline" onClick={() => go({ to: '/books' })}>
          Назад
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </FormStickyActions>
    </div>
  );
}
